import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  USER_CONFIG_TYPES,
  userConfig,
  isNullOrEmpty,
  useWhiteLabelConfig,
  useFeatureHub,
  FEATURE_FLAGS,
  WL,
  ONLINE_HELP_CONSTS,
  isSilenceToaster,
  t
} from '@cyberint/common-utils';
import { Icons, useSnackbars } from '@cyberint/dsc';

const WIDGET_WIDTH = 335;
const WIDGET_HEIGHT = 505;
const LAST_RELEASE_NOTES_DATE_KEY = 'LAST_RELEASE_NOTES_DATE';

function OnlineHelpWidget({ userId }) {
  const widgetButtonRef = useRef();
  const { flags } = useFeatureHub();
  const latestRN = flags?.[FEATURE_FLAGS.OLH_VERSION];
  const { whiteLabelConfig } = useWhiteLabelConfig();
  const { infoSnackbar, errorSnackbar } = useSnackbars();
  const [, setInitialPosition] = useState({ x: 0, y: 0 });

  const showOlhIcon = WL.showOnlineHelp(whiteLabelConfig);

  /** *************************************************************** * */

  const setInitialWidgetPosition = () => {
    if (!widgetButtonRef.current) {
      return;
    }
    const { x, y } = widgetButtonRef.current.getBoundingClientRect();
    setInitialPosition({
      x: x - WIDGET_WIDTH - 15,
      y: y - WIDGET_HEIGHT - 15
    });
  };

  useEffect(() => {
    setInitialWidgetPosition();

    if (!userId || !latestRN || isSilenceToaster()) {
      return;
    }

    const updateReleaseNotes = async ({ version, uiOptions }) => {
      const payload = {
        user_id: userId,
        ...(!isNullOrEmpty(version) ? { version } : {}),
        configuration: { ui_options: { ...uiOptions, [LAST_RELEASE_NOTES_DATE_KEY]: `${latestRN}` } },
        configuration_type: USER_CONFIG_TYPES.UI_CONFIGURATION
      };
      await userConfig.upsertUIConfiguration(payload);
    };

    const getUserConfig = async () => {
      try {
        const body = { filters: { user_id: [userId], configuration_type: [USER_CONFIG_TYPES.UI_CONFIGURATION] } };
        const { data } = await userConfig.fetchUserConfiguration(body);

        const userData = data?.data?.user_configuration_by_user_id?.[userId]?.[0] || null;
        const uiOptions = userData?.data?.ui_options;
        const releaseNotesData = uiOptions?.[LAST_RELEASE_NOTES_DATE_KEY] || null;

        if (releaseNotesData !== latestRN) {
          infoSnackbar(t('release_notes.new_release_notes'), {
            persist: true,
            link: t('release_notes.go_to_release_notes'),
            onLinkClick: () => window.open(ONLINE_HELP_CONSTS.OLH_SERVICE_RELEASE_NOTES_URL, '_blank'),
            preventDuplicate: true
          });

          await updateReleaseNotes({ version: userData?.version, uiOptions });
        }
      } catch {
        errorSnackbar(t('release_notes.failed_to_fetch_user_config'));
      }
    };

    getUserConfig();
  }, [userId, latestRN]);

  if (!showOlhIcon) return null;

  const openInNewTab = () => {
    window.open(ONLINE_HELP_CONSTS.OLH_SERVICE_DOCS_URL, '_blank');
  };

  return <Icons.OnlineHelpIcon onClick={openInNewTab} data-testid="online-help-widget-button" />;
}

OnlineHelpWidget.propTypes = {
  position: PropTypes.shape({
    top: PropTypes.string,
    right: PropTypes.string,
    bottom: PropTypes.string,
    left: PropTypes.string
  }),
  userId: PropTypes.string.isRequired
};

export default OnlineHelpWidget;
